import {
    type ReactNode
} from "react";
import type {
    SocketActionResponseTypes,
    SocketActionRequestTypes,
    ParticipationApproved,
    ServiceContextType,
    ThreeTContextType,
    SocketActionsType,
    ConnectionDetails,
    ThreeTFinished,
    ActionsType,
    ServiceType,
    NewRound
} from "../types";
import NContext, {
    type ConfigType
} from "ncore-context";
import {
    Socket,
    io
} from "socket.io-client";
import {
    storage
} from "../../../storage";
import {
    RESTService
} from "../../restAPI";
import actions from "../actions";
import {
    THREET_SOCKET_SERVER_ADDRESS
} from "../constants";
import socketActions from "../socketActions";

class ThreeTContextService extends NContext<ThreeTContextType, ConfigType<ThreeTContextType>> {
}

class ServiceContextInheritence<T extends ServiceType> extends NContext<ServiceContextType, ConfigType<ServiceContextType>> {
    ThreeTContext: ThreeTContextService;
    socketListeners: Array<Socket> = [];
    socket: Socket | null = null;
    isConnected = false;
    socketActions;
    actions: typeof actions;
    currentConnectionDetails: ConnectionDetails | null = null;
    queue: Array<{
        actionName: ActionsType;
        params: unknown;
    }> = [];

    subscribers: Array<{
        func: (params: unknown, state: ServiceContextType) => void;
        key: string;
        id?: string;
    }> = [];

    constructor(initialState: T) {
        super(
            initialState,
            {
                key: "service-context"
            }
        );

        this.socketActions = initialState.socketActions;
        this.actions = initialState.actions;

        this.ThreeTContext = new ThreeTContextService({
            isConnected: false
        }, {
            key: "threeT-context"
        });
    }

    addUniqueEventListener = (key: string, func: (payload: unknown, state: ServiceContextType) => void) => {
        if(this.subscribers.findIndex(e => e.key === key) === -1) {
            this.subscribers.push({
                func,
                key
            });
        }
    };

    emitWithKey = (key: string, payload: unknown) => {
        this.subscribers.forEach(e => {
            if(e.key === key) {
                e.func(payload, this.state);
            }
        });
    };

    emitAll = (payload: unknown) => {
        this.subscribers.forEach(e => {
            e.func(payload, this.state);
        });
    };


    removeEventListenerWithID = (id: string) => {
        const funcIndex = this.subscribers.findIndex(subscriber => subscriber.id === id);

        if(funcIndex > -1) {
            this.subscribers.splice(funcIndex, 1);
        }
    };


    addEventListenerWithID = (key: string, id: string, func: (payload: unknown, state: ServiceContextType) => void) => {
        this.subscribers.push({
            func,
            key,
            id
        });
    };


    removeListeners = () => {
        if(!this.socketListeners.length) {
            return;
        }

        this.socketListeners = [];

        const myEvents: string[] = [
            "participation-approved",
            "connection-status",
            "three-t-finished",
            "new-round"
        ];

        myEvents.forEach(event => {
            this.socket?.off(event);
        });
    };

    connect = (threetID: string) => {
        const threeTAccessToken = storage.getString("threeTAccessToken");

        if(!threeTAccessToken) {
            return;
        }

        this.socket = io(THREET_SOCKET_SERVER_ADDRESS, {
            auth: {
                authorization: threeTAccessToken,
                threetID
            }
        });

        this.socket.on("connect", () => {
            this.startSocket();
        });

        this.socket.on("reconnect", () => {
            this.startSocket();
        });

        this.socket.on("disconnect", () => {
            this.isConnected = false;

            this.ThreeTContext.setState({
                isConnected: false
            });

            this.removeListeners();
        });

        this.socket.on("error", (err) => {
            console.error("⚡ Socket error:", err);
        });
    };

    disconnect = () => {
        this.socket?.disconnect();
    };

    connectListeners = () => {
        if(!this.socket || !this.isConnected) {
            return;
        }

        const connectionListener = this.socket.on("connection-status", (data: {
            authError: boolean;
            threetID: string;
            success: boolean;
        }) => {
            if(data.authError) {
                storage.remove("threeTAccessToken");

                if(this.currentConnectionDetails) {
                    this.getAccessToken(this.currentConnectionDetails).catch(() => {
                        this.emitWithKey("auth-failed-explore", true);
                    });
                }
            }
            this.emitWithKey("connection-status", {
                ...data
            });
        });

        const participationListener = this.socket.on("participation-approved", (data: ParticipationApproved) => {
            this.emitWithKey("participation-approved", data);
        });

        const newRoundListener = this.socket.on("new-round", (data: NewRound) => {
            this.emitWithKey("new-round", data);
        });

        const finishedListener = this.socket.on("three-t-finished", (data: ThreeTFinished) => {
            this.emitWithKey("three-t-finished", data);
            this.disconnect();
        });

        this.socketListeners.push(connectionListener);
        this.socketListeners.push(participationListener);
        this.socketListeners.push(newRoundListener);
        this.socketListeners.push(finishedListener);
    };

    getAccessToken = async (params: ConnectionDetails): Promise<void> => {
        this.currentConnectionDetails = params;
        const accessToken = storage.getString("accessToken");
        const isGuest = !accessToken;

        try {
            let res;

            if(isGuest) {
                res = await RESTService.action("GetThreetTokenForGuests", {
                    phoneNumber: params.phoneNumber || "",
                    threetSubID: params.threetSubID,
                    eventCode: params.eventCode,
                    threetID: params.threetID
                });
            } else {
                res = await RESTService.action("GetThreetTokenForMembers", {
                    threetSubID: params.threetSubID,
                    eventCode: params.eventCode,
                    threetID: params.threetID
                });
            }

            storage.set("threeTAccessToken", res.payload.soketToken);
            this.connect(params.threetID);

            return Promise.resolve();
        } catch(err) {
            console.error("ThreeT Get Token Error:", err);
            return Promise.reject(err);
        }
    };

    getAccessTokenWithoutCode = async (params: { threetSubID: string; threetID: string }): Promise<void> => {
        try {
            const res = await RESTService.action("GetThreetTokenWithoutCode", {
                threetSubID: params.threetSubID,
                threetID: params.threetID
            });

            if(!res || !res.payload || !res.payload.soketToken) {
                return Promise.reject("Token bulunamadı");
            }

            storage.set("threeTAccessToken", res.payload.soketToken);
            this.connect(params.threetID);

            return Promise.resolve();
        } catch(err) {
            console.error("ThreeT Get Token Without Code Error:", err);
            return Promise.reject(err);
        }
    };

    startSocket = () => {
        this.isConnected = true;

        this.ThreeTContext.setState({
            isConnected: true
        });

        this.connectListeners();
    };

    socketAction = async <K extends SocketActionsType>(actionName: K, params: SocketActionRequestTypes[K]): Promise<Pick<SocketActionResponseTypes, K>[K] | undefined> => {
        if(!this.socket || !this.socket.connected) {
            return undefined;
        }

        const action = this.socketActions[actionName] as (p: SocketActionRequestTypes[K], s: typeof this.socket) => Promise<Pick<SocketActionResponseTypes, K>[K]>;
        return await action(params, this.socket)
            .then((res) => {
                return res;
            })
            .catch((err: unknown) => {
                console.error(err);
                return undefined;
            });
    };

    Provider = ({
        children
    }: {
        children: ReactNode;
    }) => {
        const ThreeTContext = this.ThreeTContext;

        return <ThreeTContext.Provider>
            {children}
        </ThreeTContext.Provider>;
    };
}

const ThreeTServiceInherit = new ServiceContextInheritence({
    socketActions: socketActions,
    actions: actions
});

export default ThreeTServiceInherit;
